import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { Card, Typography, Button, Tooltip } from "antd";
import {
  UnorderedListOutlined,
} from "@ant-design/icons";
import { Calculator, BarChart3, TrendingUp, Zap, Plus } from "lucide-react";
const { Title, Paragraph } = Typography;

interface CardData { 
  title: string;
  icon: React.ComponentType<any>;
  description: string;
  gradientFrom: string;
  gradientTo: string;
  gradientClass: string;
  iconGradient: string;
  badge?: string;
}

const PowerCalculatorLanding: React.FC = () => {
  const location = useLocation();
  const [animatedCards, setAnimatedCards] = useState<boolean[]>([]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatedCards(new Array(4).fill(true));
    }, 200);
    return () => clearTimeout(timer);
  }, []);

  const cardData: CardData[] = [
    {
      title: "Determine Test & Control Locations",
      icon: Calculator,
      description:
        "Identify the optimal number and selection of test and control markets needed for your geo-experiment to deliver statistically valid and actionable results.",
      gradientFrom: "#3b82f6",
      gradientTo: "#8b5cf6",
      gradientClass: "from-blue-500 to-purple-600",
      iconGradient: "linear-gradient(135deg, #3b82f6, #8b5cf6)",
      badge: "AI Powered",
    },
    {
      title: "Calculate Minimum Investment Required",
      icon: BarChart3,
      description:
        "Understand the minimum media investment needed to detect a meaningful lift in your target KPI and ensure your experiment delivers reliable business insights.",
      gradientFrom: "#10b981",
      gradientTo: "#06b6d4",
      gradientClass: "from-emerald-500 to-cyan-500",
      iconGradient: "linear-gradient(135deg, #10b981, #06b6d4)",
    },
    {
      title: "National vs. Regional Experiment Design",
      icon: TrendingUp,
      description:
        "Decide whether to run your experiment at a national or regional level based on your budget constraints while maintaining statistical significance for confident decision-making.",
      gradientFrom: "#f59e0b",
      gradientTo: "#ef4444",
      gradientClass: "from-amber-500 to-red-500",
      iconGradient: "linear-gradient(135deg, #f59e0b, #ef4444)",
      badge: "Live",
    },
    {
      title: "Optimize Experiment Duration & Scope",
      icon: Zap,
      description:
        "Balance your available budget, desired market coverage, and timeline to design an experiment that delivers credible ROI measurement without overspending.",
      gradientFrom: "#8b5cf6",
      gradientTo: "#ec4899",
      gradientClass: "from-purple-600 to-pink-500",
      iconGradient: "linear-gradient(135deg, #8b5cf6, #ec4899)",
    },
  ];

  return (
    <div className="min-h-screen bg-white py-12 px-6 md:px-20">
      <div className="text-center mb-12">
        <Title level={1}>Experiment Power Calculator</Title>
        <Paragraph className="text-lg text-gray-600 mt-4 max-w-3xl mx-auto">
          Plan smarter geo-experiments by determining the right market selection, minimum investment, and experiment scope to achieve statistically significant ROI measurement
        </Paragraph>
      </div>
      <div className="flex flex-col md:flex-row gap-6 justify-center items-center mb-12">
        <div className="relative group">
          <Link to="/power-calculator/list">
            <Button
              icon={<UnorderedListOutlined />}
              size="large"
            >
              View All Calculations
            </Button>
          </Link>
        </div>
        <div className="flex flex-col sm:flex-row gap-4">
          <Tooltip
            title="Calculate the optimal test design, budget requirements, and market selection for your next geo-experiment"
            placement="bottom"
          >
            <Link to={`/power-calculator/${uuidv4()}`}>
              <Button
                type="primary"
                icon={<Plus className="h-4 w-4" />}
                size="large"
                className="bg-gradient-to-r from-blue-600 to-purple-600 border-none hover:from-blue-700 hover:to-purple-700 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              >
                Plan New Experiment
              </Button>
            </Link>
          </Tooltip>
        </div>
      </div>

      {/* Feature Cards Section */}
      <div className="mt-16">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 gap-6">
          {cardData.map((card, index) => {
            const IconComponent = card.icon;
            const isAnimated = animatedCards[index];

            return (
              <Card
                key={index}
                className={`
                  shadow-lg hover:shadow-2xl rounded-2xl border border-gray-200
                  relative group overflow-hidden transition-all duration-700
                  transform hover:scale-105 hover:-translate-y-2
                  ${
                    isAnimated
                      ? "animate-in slide-in-from-bottom-4 fade-in"
                      : "opacity-0"
                  }
                `}
                style={{
                  animationDelay: `${index * 150}ms`,
                  animationDuration: "800ms",
                  animationFillMode: "forwards",
                }}
                styles={{ body: { padding: "24px" } }}
              >
                {/* Enhanced background effects */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <div
                    className="absolute inset-0"
                    style={{
                      background: `linear-gradient(135deg, ${card.gradientFrom}08, ${card.gradientTo}08)`,
                    }}
                  />
                  <div
                    className="absolute inset-0"
                    style={{
                      backgroundImage: `radial-gradient(circle at 20% 80%, ${card.gradientFrom}15 0%, transparent 50%), radial-gradient(circle at 80% 20%, ${card.gradientTo}15 0%, transparent 50%)`,
                    }}
                  />
                </div>

                <div
                  className="absolute top-0 left-0 right-0 h-1 transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-700 ease-out"
                  style={{ background: card.iconGradient }}
                />
                {/* Header with icon and title */}
                <div className="flex items-center gap-4 mb-4 relative z-10">
                  <div
                    className="p-3 rounded-xl transition-all duration-500 group-hover:scale-110 group-hover:rotate-3 shadow-md"
                    style={{
                      background: card.iconGradient,
                      boxShadow: `0 4px 20px ${card.gradientFrom}40`,
                    }}
                  >
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>

                  <Title
                    level={4}
                    className="text-gray-800 font-semibold mb-0 group-hover:text-gray-900 transition-colors duration-300"
                  >
                    {card.title}
                  </Title>
                </div>

                {/* Description */}
                <div className="relative z-10">
                  <Paragraph className="text-gray-600 leading-relaxed group-hover:text-gray-700 transition-colors duration-300">
                    {card.description}
                  </Paragraph>
                </div>

                {/* Hover indicator */}
                <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-x-2 group-hover:translate-x-0">
                  <div
                    className="w-2 h-2 rounded-full animate-pulse"
                    style={{ backgroundColor: card.gradientFrom }}
                  />
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PowerCalculatorLanding;
